import { z } from 'zod';
import {
  portalCheckoutSubmitSchema,
  portalOrderResultSchema,
  type PortalCheckoutSubmit,
} from '@erp/shared';
import { portalFetch } from './portal-api';
import { portalDiagnosticActor } from './portal-diagnostics';

const PENDING = 'erp-portal-pending-checkout';
const pendingSchema = z.object({
  actorId: z.string().nullable(),
  input: portalCheckoutSubmitSchema,
});

export function pendingPortalCheckout(): PortalCheckoutSubmit | null {
  try {
    const parsed = pendingSchema.safeParse(JSON.parse(sessionStorage.getItem(PENDING) ?? 'null'));
    return parsed.success && parsed.data.actorId === portalDiagnosticActor()
      ? parsed.data.input
      : null;
  } catch {
    return null;
  }
}
export function checkoutKey(cartId: string, version: number): string {
  const pending = pendingPortalCheckout();
  if (pending !== null) return pending.idempotencyKey;
  const storageKey = `erp-portal-checkout:${portalDiagnosticActor()}:${cartId}:${version}`;
  try {
    const current = localStorage.getItem(storageKey);
    if (current !== null && z.string().uuid().safeParse(current).success) return current;
    const key = crypto.randomUUID();
    localStorage.setItem(storageKey, key);
    return key;
  } catch {
    return crypto.randomUUID();
  }
}
export async function recoverPortalCheckout(key: string) {
  const order = (
    await portalFetch(
      `/api/v1/portal/checkout/${key}`,
      z.object({ order: portalOrderResultSchema.nullable() }),
    )
  ).order;
  if (order !== null && pendingPortalCheckout()?.idempotencyKey === key) {
    sessionStorage.removeItem(PENDING);
  }
  return order;
}
export async function submitPortalCheckoutSafely(input: PortalCheckoutSubmit) {
  // A failed status request must not fall through to a blind submission.
  const pending = pendingPortalCheckout();
  const order = await recoverPortalCheckout(pending?.idempotencyKey ?? input.idempotencyKey);
  if (order !== null) {
    sessionStorage.removeItem(PENDING);
    return order;
  }
  sessionStorage.setItem(PENDING, JSON.stringify({ actorId: portalDiagnosticActor(), input }));
  const result = await portalFetch('/api/v1/portal/checkout', portalOrderResultSchema, {
    method: 'POST',
    body: input,
    csrf: true,
  });
  sessionStorage.removeItem(PENDING);
  return result;
}
